import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  DocumentTextIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  MagnifyingGlassPlusIcon,
  MagnifyingGlassMinusIcon,
  SparklesIcon,
  ShieldCheckIcon,
  XCircleIcon,
  PencilSquareIcon,
} from '@heroicons/react/24/outline';

import type { Candidate, Citation, UploadedFile } from '../types';
import type { CriterionColor } from '../utils';

/**
 * Méta par citation utilisée en mode "Tout" pour colorer le surlignage
 * selon le critère d'appartenance (et non le statut). Construite en amont
 * par `ProjectWorkspace` à partir des évaluations + palette critères.
 */
export interface CitationMeta {
  criterionId: string;
  code: string;
  title: string;
  color: CriterionColor;
}

export interface LegendCriterion {
  id: string;
  code: string;
  title: string;
  color: CriterionColor;
}

interface Props {
  candidate: Candidate | null;
  files: UploadedFile[]; // fichiers déposés pour ce candidat
  citations: Citation[]; // citations à afficher (scope défini par le parent)
  activeCitationId: string | null;
  hoveredCitationId: string | null;
  onSelectCitation: (id: string | null) => void;
  onHoverCitation: (id: string | null) => void;
  /** `criterion` (défaut) : surlignage par statut. `all` : surlignage par critère. */
  mode?: 'criterion' | 'all';
  /** Requis en mode `all` pour savoir quelle couleur appliquer à chaque citation. */
  citationMeta?: Map<string, CitationMeta>;
  /** Barre de légende affichée au-dessus du document (mode `all` seulement). */
  legendCriteria?: LegendCriterion[];
}

/**
 * Faux viewer PDF : rend des "pages A4" empilées avec du texte mock.
 * Les citations sont matérialisées par des surlignages colorés selon leur état
 * (IA / vérifiée / rejetée / manuelle). Cliquer un surlignage sélectionne la
 * citation, survoler déclenche un highlight synchronisé côté éditeur.
 *
 * NB : strictement visuel — aucun parseur PDF, aucune logique de coordonnées.
 * Destiné au prototype UI uniquement.
 */
const MockPdfViewer: React.FC<Props> = ({
  candidate,
  files,
  citations,
  activeCitationId,
  hoveredCitationId,
  onSelectCitation,
  onHoverCitation,
  mode = 'criterion',
  citationMeta,
  legendCriteria,
}) => {
  const [selectedFileId, setSelectedFileId] = useState<string | null>(files[0]?.id ?? null);
  const [zoom, setZoom] = useState(1);
  const scrollRef = useRef<HTMLDivElement>(null);
  const pageRefs = useRef<Map<number, HTMLDivElement>>(new Map());

  // Si le candidat change ou que le fichier sélectionné disparaît, on bascule sur le 1er dispo
  useEffect(() => {
    if (files.length === 0) {
      setSelectedFileId(null);
      return;
    }
    if (!selectedFileId || !files.some(f => f.id === selectedFileId)) {
      setSelectedFileId(files[0].id);
    }
  }, [files, selectedFileId]);

  // Quand une citation devient active, on sélectionne son fichier + on scrolle
  useEffect(() => {
    if (!activeCitationId) return;
    const target = citations.find(c => c.id === activeCitationId);
    if (!target) return;
    if (target.sourceFileId !== selectedFileId) {
      setSelectedFileId(target.sourceFileId);
      // Le scroll sera déclenché au prochain effet (après rerender)
      return;
    }
    const pageEl = pageRefs.current.get(target.sourcePage);
    const scroller = scrollRef.current;
    if (pageEl && scroller) {
      scroller.scrollTo({ top: pageEl.offsetTop - 24, behavior: 'smooth' });
    }
  }, [activeCitationId, citations, selectedFileId]);

  const selectedFile = useMemo(
    () => files.find(f => f.id === selectedFileId) ?? null,
    [files, selectedFileId]
  );

  // Pages de ce fichier, bornées à 8 pour ne pas exploser le DOM du proto
  const pagesCount = selectedFile?.pages ? Math.min(selectedFile.pages, 8) : 3;
  const pages = useMemo(() => Array.from({ length: pagesCount }, (_, i) => i + 1), [pagesCount]);

  // Citations pour le fichier courant
  const fileCitations = useMemo(
    () => citations.filter(c => c.sourceFileId === selectedFileId),
    [citations, selectedFileId]
  );

  if (!candidate) {
    return (
      <div className="h-full flex flex-col items-center justify-center p-8 text-center bg-gray-50">
        <DocumentTextIcon className="w-12 h-12 text-gray-300 mb-3" />
        <p className="text-sm text-gray-600">Sélectionnez un candidat pour visualiser son mémoire.</p>
      </div>
    );
  }

  if (files.length === 0) {
    return (
      <div className="h-full flex flex-col items-center justify-center p-8 text-center bg-gray-50">
        <DocumentTextIcon className="w-12 h-12 text-gray-300 mb-3" />
        <h3 className="text-sm font-semibold text-gray-900 mb-1">
          Aucun mémoire déposé pour {candidate.name}
        </h3>
        <p className="text-xs text-gray-600 max-w-xs">
          Glissez-déposez le PDF du mémoire technique depuis la barre du haut pour commencer.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-gray-100">
      {/* Barre supérieure : fichier courant + zoom + navigation */}
      <div className="shrink-0 flex items-center gap-2 px-3 py-2 bg-white border-b border-gray-200">
        <div className="flex-1 min-w-0 flex items-center gap-2">
          <DocumentTextIcon className="shrink-0 w-4 h-4 text-gray-500" />
          {files.length === 1 ? (
            <span className="text-sm text-gray-800 truncate" title={selectedFile?.name}>
              {selectedFile?.name}
            </span>
          ) : (
            <select
              value={selectedFileId ?? ''}
              onChange={e => setSelectedFileId(e.target.value)}
              className="flex-1 min-w-0 px-2 py-1 text-sm text-gray-800 border border-gray-200 rounded-lg bg-white hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500"
            >
              {files.map(f => (
                <option key={f.id} value={f.id}>
                  {f.name}
                </option>
              ))}
            </select>
          )}
          <span className="shrink-0 text-xs text-gray-500 tabular-nums">
            {pagesCount} p.
          </span>
        </div>
        <div className="shrink-0 flex items-center gap-0.5 border border-gray-200 rounded-lg">
          <button
            type="button"
            onClick={() => setZoom(z => Math.max(0.7, z - 0.1))}
            className="p-1.5 text-gray-500 hover:bg-gray-50 hover:text-gray-700 transition-colors rounded-l-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500"
            aria-label="Zoom arrière"
          >
            <MagnifyingGlassMinusIcon className="w-4 h-4" />
          </button>
          <span className="px-2 text-xs text-gray-600 tabular-nums min-w-[3rem] text-center">
            {Math.round(zoom * 100)}%
          </span>
          <button
            type="button"
            onClick={() => setZoom(z => Math.min(1.5, z + 0.1))}
            className="p-1.5 text-gray-500 hover:bg-gray-50 hover:text-gray-700 transition-colors rounded-r-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500"
            aria-label="Zoom avant"
          >
            <MagnifyingGlassPlusIcon className="w-4 h-4" />
          </button>
        </div>
        <div className="shrink-0 flex items-center gap-0.5">
          <button
            type="button"
            onClick={() => {
              const scroller = scrollRef.current;
              if (!scroller) return;
              scroller.scrollBy({ top: -scroller.clientHeight * 0.9, behavior: 'smooth' });
            }}
            className="p-1.5 text-gray-500 hover:bg-gray-50 hover:text-gray-700 rounded-lg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500"
            aria-label="Page précédente"
          >
            <ChevronLeftIcon className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => {
              const scroller = scrollRef.current;
              if (!scroller) return;
              scroller.scrollBy({ top: scroller.clientHeight * 0.9, behavior: 'smooth' });
            }}
            className="p-1.5 text-gray-500 hover:bg-gray-50 hover:text-gray-700 rounded-lg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500"
            aria-label="Page suivante"
          >
            <ChevronRightIcon className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Barre légende (mode "Tout") : associe chaque couleur à son critère.
          Un survol d'un élément met en évidence les citations correspondantes. */}
      {mode === 'all' && legendCriteria && legendCriteria.length > 0 && (
        <div className="shrink-0 flex items-center gap-1.5 px-3 py-1.5 bg-gray-50 border-b border-gray-200 overflow-x-auto">
          <span className="shrink-0 text-[10px] font-medium text-gray-500 uppercase tracking-wide mr-1">
            Légende
          </span>
          {legendCriteria.map(c => (
            <span
              key={c.id}
              title={`${c.code} · ${c.title}`}
              className={`shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] font-medium ${c.color.softBg} ${c.color.text}`}
            >
              <span className={`w-2 h-2 rounded-full ${c.color.dot}`} aria-hidden />
              <span className="tabular-nums">{c.code}</span>
            </span>
          ))}
        </div>
      )}

      {/* Zone scrollable des pages */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto py-6 px-4"
        onClick={() => onSelectCitation(null)}
      >
        <div
          className="mx-auto space-y-6"
          style={{ width: `${Math.round(640 * zoom)}px`, maxWidth: '100%' }}
        >
          {pages.map(pageNum => {
            const pageCitations = fileCitations.filter(c => c.sourcePage === pageNum);
            return (
              <MockPage
                key={pageNum}
                ref={el => {
                  if (el) pageRefs.current.set(pageNum, el);
                  else pageRefs.current.delete(pageNum);
                }}
                pageNum={pageNum}
                totalPages={selectedFile?.pages ?? pagesCount}
                citations={pageCitations}
                activeCitationId={activeCitationId}
                hoveredCitationId={hoveredCitationId}
                onSelectCitation={onSelectCitation}
                onHoverCitation={onHoverCitation}
                mode={mode}
                citationMeta={citationMeta}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────
// Page mock (A4 avec texte lorem + citations surlignées)
// ─────────────────────────────────────────────────────────────────

interface MockPageProps {
  pageNum: number;
  totalPages: number;
  citations: Citation[];
  activeCitationId: string | null;
  hoveredCitationId: string | null;
  onSelectCitation: (id: string | null) => void;
  onHoverCitation: (id: string | null) => void;
  mode: 'criterion' | 'all';
  citationMeta?: Map<string, CitationMeta>;
}

const MOCK_PARAGRAPHS = [
  'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Nam porttitor, metus eu cursus efficitur, nulla urna rutrum nisi, ut porta tellus massa vel sapien. Pellentesque habitant morbi tristique senectus et netus et malesuada fames ac turpis egestas.',
  'Pellentesque euismod, lorem sed tincidunt bibendum, ante risus gravida tortor, at laoreet purus purus quis ligula. In hac habitasse platea dictumst. Sed vitae commodo nibh, non malesuada metus. Nulla facilisi.',
  'Ut dapibus, ipsum ut dignissim finibus, ligula ligula euismod tellus, nec accumsan enim massa eget nulla. Mauris porttitor sem a libero congue, nec dictum risus porta. Proin convallis, ante nec consequat ornare, augue leo dignissim nibh.',
  'Curabitur in sapien quis nunc scelerisque suscipit. Vivamus et facilisis velit. Nullam tempor bibendum consequat. Duis pellentesque, purus a tincidunt pharetra, tortor orci vulputate justo, vitae bibendum leo arcu eget eros.',
  'Aenean rutrum massa vitae ante fringilla, vel lobortis nibh cursus. Suspendisse potenti. Morbi porttitor lacinia ante, at hendrerit neque fringilla at. Proin vel massa eu orci sagittis pretium.',
  'Fusce laoreet, magna a iaculis convallis, mi sapien cursus tellus, sit amet ultrices ipsum tellus eget ipsum. Praesent ut laoreet neque. Aenean eu fringilla quam, vel interdum velit.',
];

const MockPage = React.forwardRef<HTMLDivElement, MockPageProps>(
  (
    {
      pageNum,
      totalPages,
      citations,
      activeCitationId,
      hoveredCitationId,
      onSelectCitation,
      onHoverCitation,
      mode,
      citationMeta,
    },
    ref
  ) => {
    // On répartit les citations dans les paragraphes en alternant (modulo)
    const byParagraph = new Map<number, Citation[]>();
    citations.forEach((c, i) => {
      const p = i % MOCK_PARAGRAPHS.length;
      const bucket = byParagraph.get(p) ?? [];
      bucket.push(c);
      byParagraph.set(p, bucket);
    });

    return (
      <div
        ref={ref}
        className="relative bg-white rounded shadow-sm border border-gray-200"
        style={{ aspectRatio: '1 / 1.414' }}
      >
        {/* En-tête de page */}
        <div className="flex items-center justify-between px-10 pt-8 pb-2 text-[10px] text-gray-400 uppercase tracking-wide">
          <span>Mémoire technique · extrait de démonstration</span>
          <span className="tabular-nums">
            Page {pageNum} / {totalPages}
          </span>
        </div>

        <div className="px-10 py-6 space-y-4 text-[13px] text-gray-800 leading-relaxed">
          {MOCK_PARAGRAPHS.map((text, i) => {
            const cits = byParagraph.get(i) ?? [];
            if (cits.length === 0) {
              return (
                <p key={i} className="text-justify">
                  {text}
                </p>
              );
            }
            return (
              <p key={i} className="text-justify">
                {text}{' '}
                {cits.map(cit => {
                  const isActive = cit.id === activeCitationId;
                  const isHovered = cit.id === hoveredCitationId;
                  const meta = mode === 'all' ? citationMeta?.get(cit.id) : undefined;
                  const tone = citationTone(cit, meta);
                  return (
                    <mark
                      key={cit.id}
                      onClick={e => {
                        e.stopPropagation();
                        onSelectCitation(cit.id);
                      }}
                      onMouseEnter={() => onHoverCitation(cit.id)}
                      onMouseLeave={() => onHoverCitation(null)}
                      className={`${tone.bg} ${tone.text} cursor-pointer rounded px-0.5 ${
                        isActive ? 'ring-2 ring-violet-500 ring-offset-1' : ''
                      } ${isHovered && !isActive ? 'ring-1 ring-violet-300' : ''} ${
                        tone.ringWhenInactive && !isActive && !isHovered ? tone.ringWhenInactive : ''
                      } transition-all`}
                      title={tone.label}
                    >
                      <span className="inline-flex items-center gap-0.5 mr-0.5 align-middle">
                        {tone.icon}
                      </span>
                      « {cit.text} »
                    </mark>
                  );
                })}
              </p>
            );
          })}
        </div>
      </div>
    );
  }
);
MockPage.displayName = 'MockPage';

// ─────────────────────────────────────────────────────────────────

interface Tone {
  bg: string;
  text: string;
  label: string;
  icon: React.ReactNode;
  /** Anneau discret pour marquer le statut quand la couleur de fond encode déjà le critère. */
  ringWhenInactive?: string;
}

function citationTone(cit: Citation, meta?: CitationMeta): Tone {
  // ── Mode "Tout" : fond = couleur critère, icône + anneau = statut ──
  if (meta) {
    const statusIcon = cit.verified ? (
      <ShieldCheckIcon className="w-3 h-3 text-emerald-700" />
    ) : cit.origin === 'manual' ? (
      <PencilSquareIcon className="w-3 h-3 text-violet-700" />
    ) : (
      <SparklesIcon className="w-3 h-3 text-amber-700" />
    );
    const statusLabel = cit.verified
      ? 'Citation vérifiée'
      : cit.origin === 'manual'
      ? 'Citation manuelle'
      : 'Proposition IA à vérifier';
    return {
      bg: meta.color.bg,
      text: meta.color.text,
      label: `${meta.code} · ${meta.title} — ${statusLabel}`,
      icon: statusIcon,
      // Vérifiée : anneau vert pour garder le signal statut par-dessus la couleur critère.
      ringWhenInactive: cit.verified ? 'ring-1 ring-emerald-500' : undefined,
    };
  }

  // ── Mode "criterion" (défaut) : fond = statut ──
  if (cit.verified) {
    return {
      bg: 'bg-emerald-100',
      text: 'text-emerald-900',
      label: 'Citation vérifiée',
      icon: <ShieldCheckIcon className="w-3 h-3 text-emerald-700" />,
    };
  }
  if (cit.origin === 'manual') {
    return {
      bg: 'bg-violet-100',
      text: 'text-violet-900',
      label: 'Citation manuelle',
      icon: <PencilSquareIcon className="w-3 h-3 text-violet-700" />,
    };
  }
  // Par défaut : IA en attente de vérification
  return {
    bg: 'bg-amber-100',
    text: 'text-amber-900',
    label: 'Proposition IA à vérifier',
    icon: <SparklesIcon className="w-3 h-3 text-amber-700" />,
  };
}

// Composant "reject" (si on veut l'utiliser plus tard) — gardé pour cohérence
export const RejectedIcon: React.FC<{ className?: string }> = ({ className = '' }) => (
  <XCircleIcon className={className} />
);

export default MockPdfViewer;
